import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CommentLike {
  id: string;
  comment_id: string;
  user_id: string;
  created_at: string;
}

export function useCommentLikes(commentId: string) {
  const [likes, setLikes] = useState<CommentLike[]>([]);
  const [userLike, setUserLike] = useState<CommentLike | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchLikes = async () => {
    try {
      const { data, error } = await supabase
        .from('comment_likes')
        .select('*')
        .eq('comment_id', commentId);

      if (error) throw error;

      setLikes(data || []);
      
      if (user) {
        const userLikeData = data?.find(like => like.user_id === user.id);
        setUserLike(userLikeData || null);
      }
    } catch (error) {
      console.error('Error fetching comment likes:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async () => {
    if (!user) return false;

    try {
      if (userLike) {
        // Unlike
        const { error } = await supabase
          .from('comment_likes')
          .delete()
          .eq('id', userLike.id);

        if (error) throw error;

        setLikes(prev => prev.filter(like => like.id !== userLike.id));
        setUserLike(null);
      } else {
        // Like
        const { data, error } = await supabase
          .from('comment_likes')
          .insert([
            {
              comment_id: commentId,
              user_id: user.id
            }
          ])
          .select()
          .single();

        if (error) throw error;

        setLikes(prev => [...prev, data]);
        setUserLike(data);
      }

      return true;
    } catch (error) {
      console.error('Error toggling like:', error);
      return false;
    }
  };

  useEffect(() => {
    if (commentId) {
      fetchLikes();
    }
  }, [commentId, user?.id]);

  return {
    likes,
    userLike,
    loading,
    toggleLike,
    likesCount: likes.length,
    isLiked: !!userLike
  };
}